import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquare, Send, Trash2, Check, X, Edit3 } from "lucide-react";
import { taskCommentsApi, projectMembersApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MentionsTextarea, { renderMentions } from "@/components/MentionsTextarea";

// TaskCommentThread — Feb 2026.
//
// Embedded inside PlanTaskDialog. Renders a flat list of comments scoped to a
// single plan task, with a composer at the bottom. Edits and deletes are
// permitted on your own comments; admins can delete any.
//
// Optimistic updates throughout — the dialog feels instant, falls back if
// the API rejects.

const MAX_TEXT = 4000;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function TaskCommentThread({ taskId, projectId, canEdit }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [comments, setComments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editMentions, setEditMentions] = useState([]);
  const scrollAnchor = useRef(null);

  useEffect(() => {
    if (!taskId) return;
    let mounted = true;
    setLoading(true);
    Promise.all([
      taskCommentsApi.list(taskId).catch(() => []),
      projectId
        ? projectMembersApi
            .list(projectId)
            .then((rows) =>
              (rows || []).map((r) => ({
                id: r.user_id || r.id,
                name: r.name,
                email: r.email,
              })),
            )
            .catch(() => [])
        : Promise.resolve([]),
    ]).then(([cs, ms]) => {
      if (!mounted) return;
      setComments(cs || []);
      setMembers(ms || []);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [taskId, projectId]);

  useEffect(() => {
    // Scroll to bottom on new comment
    scrollAnchor.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [comments.length]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    if (trimmed.length > MAX_TEXT) {
      toast.error(`Comment too long (max ${MAX_TEXT} chars)`);
      return;
    }
    setSending(true);
    // Optimistic insert
    const temp = {
      id: `__tmp_${Date.now()}`,
      task_id: taskId,
      text: trimmed,
      mentions,
      author_user_id: user?.id || "",
      author_name: user?.name || "",
      author_email: user?.email || "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      edited: false,
    };
    setComments((prev) => [...prev, temp]);
    setText("");
    setMentions([]);
    try {
      const created = await taskCommentsApi.create(taskId, trimmed, mentions);
      setComments((prev) => prev.map((c) => (c.id === temp.id ? created : c)));
    } catch (err) {
      setComments((prev) => prev.filter((c) => c.id !== temp.id));
      toast.error(err?.response?.data?.detail || "Failed to post comment");
    } finally {
      setSending(false);
    }
  };

  const handleStartEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.text);
    setEditMentions(c.mentions || []);
  };

  const handleSaveEdit = async () => {
    const trimmed = editText.trim();
    if (!trimmed) {
      toast.error("Comment cannot be empty");
      return;
    }
    const prev = comments;
    setComments((cur) =>
      cur.map((c) =>
        c.id === editingId
          ? { ...c, text: trimmed, mentions: editMentions, edited: true, updated_at: new Date().toISOString() }
          : c,
      ),
    );
    const id = editingId;
    setEditingId(null);
    setEditText("");
    setEditMentions([]);
    try {
      await taskCommentsApi.edit(id, trimmed, editMentions);
    } catch (err) {
      setComments(prev);
      toast.error(err?.response?.data?.detail || "Failed to edit");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    const prev = comments;
    setComments((cur) => cur.filter((x) => x.id !== c.id));
    try {
      await taskCommentsApi.remove(c.id);
    } catch (err) {
      setComments(prev);
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="border-t border-zinc-800 pt-5 mt-2" data-testid="task-comment-thread">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-overline text-zinc-500 flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3" /> Comments
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500" data-testid="comment-count">{comments.length}</span>
        </Label>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : comments.length === 0 ? (
        <div className="text-xs italic text-zinc-600 py-3">
          No comments yet. Start the conversation.
        </div>
      ) : (
        <ul className="space-y-3 max-h-72 overflow-y-auto pr-1" data-testid="comment-list">
          {comments.map((c) => {
            const isMine = c.author_user_id === user?.id;
            const canDelete = isMine || isAdmin;
            const isEditing = editingId === c.id;
            return (
              <li
                key={c.id}
                data-testid={`comment-${c.id}`}
                className="group bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-xs font-medium text-zinc-200 truncate">
                      {c.author_name || c.author_email || "Unknown"}
                    </span>
                    <span className="text-[10px] text-zinc-600 font-mono shrink-0">
                      {timeAgo(c.created_at)}
                      {c.edited && " · edited"}
                    </span>
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      {isMine && (
                        <button
                          type="button"
                          data-testid={`comment-edit-${c.id}`}
                          onClick={() => handleStartEdit(c)}
                          className="text-zinc-600 hover:text-amber-400 p-1"
                          aria-label="Edit"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          data-testid={`comment-delete-${c.id}`}
                          onClick={() => handleDelete(c)}
                          className="text-zinc-600 hover:text-rose-400 p-1"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <MentionsTextarea
                      value={editText}
                      onChange={(v) => setEditText(v.slice(0, MAX_TEXT))}
                      onMentionsChange={setEditMentions}
                      members={members}
                      rows={3}
                      wrapperClassName="w-full"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60 resize-none"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        className="text-xs bg-amber-500 text-black hover:bg-amber-400 rounded-sm px-2 py-1 flex items-center gap-1 font-medium"
                      >
                        <Check className="w-3 h-3" /> Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setEditText("");
                          setEditMentions([]);
                        }}
                        className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">
                    {renderMentions(c.text, members)}
                  </p>
                )}
              </li>
            );
          })}
          <div ref={scrollAnchor} />
        </ul>
      )}

      {/* Composer.
          NOTE: This MUST be a <div>, not a <form>. The whole thread renders
          inside PlanTaskDialog's <form onSubmit={handleSave}>, and nested
          forms are invalid HTML. With a nested form, clicking the comment
          Send button actually fires the OUTER dialog form's submit handler
          — saving the task and closing the dialog before the comment
          create call completes. */}
      {canEdit && (
        <div className="mt-3 flex gap-2 items-end">
          <MentionsTextarea
            data-testid="comment-input"
            value={text}
            onChange={(v) => setText(v.slice(0, MAX_TEXT))}
            onMentionsChange={setMentions}
            members={members}
            placeholder="Add a comment… (@ to mention · ⌘/Ctrl+Enter to send)"
            rows={2}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }
            }}
            wrapperClassName="flex-1 min-w-0"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60 resize-none"
            disabled={sending}
          />
          <button
            type="button"
            data-testid="comment-send-btn"
            disabled={!text.trim() || sending}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }}
            className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm px-3 py-2 transition-colors flex items-center gap-1 text-sm font-medium"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}

// Tiny inline import so we don't pull the whole Label component in just for the heading
function Label({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}
